// @ts-nocheck
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getPageLinkByNodeId } from "@insite/client-framework/Store/Links/LinksSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import constants from "@store/constants";
import getTextAlignmentField, { textAlignmentOptions } from "@store/Framework/Common/Fields/Toolkit/textAlignment";
import { useBlogHomeMostRecentDataView } from "@store/Framework/Store/Data/BlogHome/BlogHomeHooks";
import loadBlogMostRecent from "@store/Framework/Store/Starter/Data/Blog/Handlers/LoadBlogMostRecent";
import BlogTileList, { BlogTileListPresentationProps } from "@store/Library/Components/Toolkit/Blog/BlogTileList";
import DataViewLoadingOverlay from "@store/Library/Components/Toolkit/DataViewLoadingOverlay";
import { LoadingOverlayProps } from "@store/MobiusOverrides/LoadingOverlay/LoadingOverlay";
import { linkActionStyles } from "@store/Themes/common/mixins/link";
import { blogProps } from "@store/Themes/common/props/blog";
import { typographyGetNoResultsProps } from "@store/Themes/common/props/typography";
import usePersistentDataView from "@toolkit/Framework/Common/Hooks/usePersistentDataView";
import cssProp from "@toolkit/Framework/Common/Utilities/cssProp";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { useCurrentPage } from "@toolkit/Framework/Store/Data/Pages/PageHooks";
import { usePageLinkByNodeId } from "@toolkit/Framework/Store/Links/LinksHooks";
import { PageChildrenSelectorFieldValue } from "@toolkit/Framework/Types/FieldDefinition";
import { spacingStyles } from "@toolkit/Themes/common/mixins";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { css } from "styled-components";

const enum fields {
    quantity = "quantity",
    subheading = "subheading",
    title = "title",
    titleAlignment = "titleAlignment",
    blogPage = "blogPage",
    showBlogPageLink = "showBlogPageLink",
    blogPageLinkLabel = "blogPageLinkLabel",
}

const titleAlignmentField = getTextAlignmentField(fields.titleAlignment);

interface BlogHomeMostRecentProps extends WidgetProps {
    fields: {
        [fields.quantity]: number;
        [fields.subheading]: string;
        [fields.title]: string;
        [fields.titleAlignment]: string;
        [fields.blogPage]: PageChildrenSelectorFieldValue;
        [fields.showBlogPageLink]: boolean;
        [fields.blogPageLinkLabel]: string;
    };
}

export interface BlogHomeMostRecentStyles {
    title?: TypographyPresentationProps;
    loadingOverlay?: LoadingOverlayProps;
    noResultsContainer?: InjectableCss;
    noResultsText?: TypographyProps;
    subheading?: TypographyPresentationProps;
    blogPageLinkWrapper?: InjectableCss;
    blogPageLink?: LinkPresentationProps;
    blogTileList?: BlogTileListPresentationProps;
}

export const blogHomeMostRecentStyles: BlogHomeMostRecentStyles = {
    title: { variant: "h2" },
    noResultsText: typographyGetNoResultsProps(),
    subheading: blogProps.blockSubheading,
    blogPageLinkWrapper: {
        css: css`
            ${spacingStyles(pxToRem(-12))}
        `,
    },
    blogPageLink: {
        css: css`
            ${linkActionStyles()}
        `,
    },
};

const styles = blogHomeMostRecentStyles;

const BlogHomeMostRecent: FC<BlogHomeMostRecentProps> = ({ fields }) => {
    const {
        blogPage,
        blogPageLinkLabel,
        quantity,
        showBlogPageLink,
        subheading,
        title,
        titleAlignment: baseTitleAlignment,
    } = fields;
    const titleAlignment = titleAlignmentField.parse(baseTitleAlignment);
    const { value: blogPageNodeId } = blogPage;
    const [cacheInvalidationId] = useState(`cacheId-${uniqueId()}`);
    const { isInShell } = useShellContext();
    const dispatch = useDispatch();
    const currentPage = useCurrentPage();
    const blogPageLink = usePageLinkByNodeId(blogPageNodeId);
    const isHomePage = currentPage.type === constants.pageType.home;
    const baseBlogMostRecentDataView = useBlogHomeMostRecentDataView(
        isHomePage ? blogPageNodeId : currentPage.nodeId,
        quantity,
        isInShell ? cacheInvalidationId : undefined,
    );
    const blogMostRecentDataView = usePersistentDataView(baseBlogMostRecentDataView);
    const { isLoading, value: articles } = blogMostRecentDataView;
    const shouldLoadArticles =
        (!isLoading && !articles) ||
        (typeof articles !== "undefined" && quantity <= articles.length && !isLoading) ||
        blogPageNodeId;

    useEffect(() => {
        if (shouldLoadArticles) {
            dispatch(
                loadBlogMostRecent({
                    nodeId: isHomePage ? blogPageNodeId : currentPage.nodeId,
                    quantity,
                    cacheInvalidationId: isInShell ? cacheInvalidationId : undefined,
                }),
            );
        }
    }, [quantity, blogPageNodeId, isHomePage]);

    const links: SafeDictionary<PageLinkModel> =
        useAppSelector(state =>
            articles?.reduce(
                (links, page) => ({
                    ...links,
                    [page.nodeId]: getPageLinkByNodeId(state, page.nodeId),
                    [page.parentId]: getPageLinkByNodeId(state, page.parentId),
                }),
                {},
            ),
        ) || {};

    const titleStyles = useMemo(() => {
        return mergeProps<BlogHomeMostRecentStyles["title"]>(styles.title, {
            css: css`
                ${cssProp("text-align", titleAlignment)}
            `,
        });
    }, [styles.title, titleAlignment]);

    const noResultsTextStyles = useMemo(() => {
        return mergeProps<BlogHomeMostRecentStyles["noResultsText"]>(styles.noResultsText, {
            css: css`
                ${cssProp("text-align", titleAlignment)}
            `,
        });
    }, [styles.noResultsText, titleAlignment]);

    const subheadingStyles = useMemo(() => {
        return mergeProps<BlogHomeMostRecentStyles["subheading"]>(styles.subheading, {
            css: css`
                ${cssProp("text-align", titleAlignment)}
            `,
        });
    }, [styles.subheading, titleAlignment]);

    const blogPageLinkWrapperStyles = useMemo(() => {
        return mergeProps<BlogHomeMostRecentStyles["blogPageLinkWrapper"]>(styles.blogPageLinkWrapper, {
            css: css`
                ${cssProp("text-align", titleAlignment)}
            `,
        });
    }, [styles.blogPageLinkWrapper, titleAlignment]);

    return (
        <>
            <Typography {...titleStyles}>{title}</Typography>
            <DataViewLoadingOverlay {...styles.loadingOverlay} dataView={blogMostRecentDataView}>
                {!articles?.length ? (
                    <StyledWrapper {...styles.noResultsContainer}>
                        <Typography {...noResultsTextStyles}>{siteMessage("Blog_Articles_Not_Found")}</Typography>
                    </StyledWrapper>
                ) : (
                    <>
                        {subheading ? <Typography {...subheadingStyles}>{subheading}</Typography> : null}
                        {showBlogPageLink && blogPageLink && (
                            <StyledWrapper {...blogPageLinkWrapperStyles}>
                                <Link {...styles.blogPageLink} href={blogPageLink.url}>
                                    {blogPageLinkLabel}
                                </Link>
                            </StyledWrapper>
                        )}
                        <BlogTileList {...styles.blogTileList} articles={articles} articlesLinks={links} />
                    </>
                )}
            </DataViewLoadingOverlay>
        </>
    );
};

const widgetModule: WidgetModule<Toolkit.FieldDefinition> = {
    component: BlogHomeMostRecent,
    definition: {
        displayName: "Most Recent",
        group: constants.widgetGroup.blog,
        allowedContexts: [constants.pageType.blogHomePage, constants.pageType.home],
        fieldDefinitions: [
            {
                name: fields.blogPage,
                displayName: "Select a Blog page",
                editorTemplate: "PageChildrenSelectorField",
                fieldType: "Contextual",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                showOnlyFirstLevelChildren: true,
                showOnlyPagesFromPageType: constants.pageType.blogHomePage,
                isRequired: true,
                isVisible: (item, advancedFeaturesEnabled, theme, pageType) =>
                    pageType !== constants.pageType.blogHomePage &&
                    pageType !== constants.pageType.blogCategoryPage &&
                    pageType !== constants.pageType.blogArticlePage,
            },
            {
                name: fields.quantity,
                displayName: "Number of articles to show",
                editorTemplate: "IntegerField",
                fieldType: "Contextual",
                defaultValue: 6,
                min: 1,
            },
            {
                name: fields.title,
                editorTemplate: "TextField",
                fieldType: "Contextual",
                defaultValue: "Most Recent",
                isRequired: true,
            },
            {
                ...titleAlignmentField.definition,
                displayName: "Title Alignment",
                fieldType: "Contextual",
                defaultValue: textAlignmentOptions.center.id,
            },
            {
                name: fields.subheading,
                editorTemplate: "TextField",
                fieldType: "Contextual",
                defaultValue: "",
            },

            {
                name: fields.showBlogPageLink,
                displayName: "Show Link to Blog Page",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Contextual",
                isVisible: (item, advancedFeaturesEnabled, theme, pageType) =>
                    pageType !== constants.pageType.blogHomePage &&
                    pageType !== constants.pageType.blogCategoryPage &&
                    pageType !== constants.pageType.blogArticlePage,
            },
            {
                name: fields.blogPageLinkLabel,
                displayName: "Link to Blog Page Label",
                editorTemplate: "TextField",
                fieldType: "Contextual",
                defaultValue: "Visit Our Blog",
                isRequired: true,
                isVisible: (item, advancedFeaturesEnabled, theme, pageType) =>
                    pageType !== constants.pageType.blogHomePage &&
                    pageType !== constants.pageType.blogCategoryPage &&
                    pageType !== constants.pageType.blogArticlePage,
            },
        ],
    },
};

export default widgetModule;
